import React from 'react';
import type { ReportTheme } from '../../../../types/reportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { EditorialFigureCaption } from './EditorialFigureCaption';

export interface EditorialExhibitProps {
  theme: ReportTheme;
  children: React.ReactNode;
  /**
   * If 'columns', the exhibit spans all flow columns (data-span-columns).
   * If 'in-column', the exhibit is kept inside the column (data-keep-in-column).
   */
  span?: 'columns' | 'in-column';
  caption?: React.ReactNode;
  captionNumber?: number;
  captionPrefix?: string;
  frame?: boolean;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
}

export function EditorialExhibit({
  theme,
  children,
  span,
  caption,
  captionNumber,
  captionPrefix,
  frame = false,
  id,
  className,
  style,
}: EditorialExhibitProps): React.ReactElement {
  return (
    <figure
      id={id}
      className={className}
      data-figure="true"
      // P-GROUP Phase A (`exhibit`) — figure + caption + explainer are ONE
      // fragmentation atom in the paged deck, so a caption can never separate
      // from its figure across a page seam. A taller-than-page exhibit is
      // handled by the EXISTING scale-to-fit pass (scaleOversizedSpanExhibits
      // already matches `figure`/`[data-figure]`; legibility floor 0.8 ≈ the
      // plan's ~80% page-height cap) — reused, not rebuilt.
      data-layout-group="exhibit"
      data-span-columns={span === 'columns' ? 'true' : undefined}
      data-keep-in-column={span === 'in-column' ? 'true' : undefined}
      style={{
        margin: 0,
        padding: 0,
        ...style,
      }}
    >
      <div
        style={
          frame
            ? {
                border: `1px solid ${roleColor(theme, 'hair')}`,
                borderRadius: 0, // Enforce no rounded corners for the journal aesthetic
              }
            : undefined
        }
      >
        {children}
      </div>
      {caption && captionNumber !== undefined && (
        <EditorialFigureCaption
          theme={theme}
          number={captionNumber}
          prefix={captionPrefix}
          as="figcaption"
        >
          {caption}
        </EditorialFigureCaption>
      )}
    </figure>
  );
}
